import React, { useEffect, useMemo, useState } from 'react';
import { addDays, endOfWeek, format, isWithinInterval, parseISO, startOfWeek } from 'date-fns';
import { employeeApi, scheduleApi, timecardApi } from '../services/api';
import { Employee, Schedule, Timecard, TimecardDay } from '../types';
import { useWeekStartDay } from '../hooks/useWeekStartDay';
import { useLocationContext } from '../components/Layout';

type ScheduleFilter = number | 'all';

const getTwoLetterCode = (name: string | null | undefined): string => {
  if (!name) return '';
  const trimmed = name.trim();
  if (!trimmed) return '';
  const words = trimmed.split(/\s+/).filter(word => word.length > 0);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  const word = words[0].toUpperCase();
  return word.length >= 2 ? word.substring(0, 2) : word.padEnd(2, 'X');
};

const getScheduledHours = (day: TimecardDay): number => {
  if (!day.scheduledIn || !day.scheduledOut) {
    return 0;
  }
  const start = parseISO(day.scheduledIn);
  const end = parseISO(day.scheduledOut);
  let totalMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
  // Overnight shift: ScheduledOut earlier than ScheduledIn
  if (totalMinutes <= 0) {
    totalMinutes += 24 * 60;
  }
  const breakPaid = day.paidBreakPaid ?? false;
  if (!breakPaid) {
    totalMinutes -= day.scheduledBreakMinutes ?? 0;
  }
  return Math.round(Math.max(0, totalMinutes / 60) * 100) / 100;
};

const getActualPaidHours = (day: TimecardDay): number => {
  if (day.totalHours !== null && day.totalHours !== undefined) {
    return Number(day.totalHours) || 0;
  }
  return 0;
};

const getEmployeeLocationId = (employee: Employee): number | null => {
  return (
    employee.locationId ??
    employee.profile?.mainLocationId ??
    employee.profile?.mainLocation?.id ??
    employee.location?.id ??
    null
  );
};

const CircleCard: React.FC<{
  title: string;
  hours: number;
  subtitle: string;
  totalHours?: number;
  highlightHours?: number;
  lightColor: string;
  darkColor: string;
}> = ({ title, hours, subtitle, totalHours, highlightHours, lightColor, darkColor }) => {
  const percent = totalHours && highlightHours !== undefined && totalHours > 0
    ? Math.min(100, (highlightHours / totalHours) * 100)
    : 100;
  const ringStyle =
    totalHours && highlightHours !== undefined && totalHours > 0
      ? { background: `conic-gradient(${darkColor} 0 ${percent}%, ${lightColor} ${percent}% 100%)` }
      : { background: lightColor };

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="text-sm font-semibold text-neutral-700">{title}</div>
      <div className="relative w-64 h-64 rounded-full p-3" style={ringStyle}>
        <div className="absolute inset-5 rounded-full bg-white flex flex-col items-center justify-center text-center">
          <div className="text-2xl font-bold text-neutral-900">{hours.toFixed(2)}h</div>
          <div className="text-sm text-neutral-500">{subtitle}</div>
        </div>
      </div>
    </div>
  );
};

const PlanVsActual: React.FC = () => {
  const { selectedLocationId } = useLocationContext();
  const weekStartsOn = useWeekStartDay();
  const [currentWeek, setCurrentWeek] = useState<Date>(() =>
    startOfWeek(new Date(), { weekStartsOn })
  );
  const weekStart = useMemo(() => startOfWeek(currentWeek, { weekStartsOn }), [currentWeek, weekStartsOn]);
  const weekEnd = useMemo(() => endOfWeek(currentWeek, { weekStartsOn }), [currentWeek, weekStartsOn]);

  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState<ScheduleFilter>('all');
  const [timecards, setTimecards] = useState<Timecard[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoveredDay, setHoveredDay] = useState<string | null>(null);

  useEffect(() => {
    const loadSchedules = async () => {
      try {
        const scheduleData = await scheduleApi.getAll();
        setSchedules(scheduleData.filter(schedule => schedule.isActive));
      } catch (err) {
        console.error('Error loading schedules:', err);
      }
    };
    loadSchedules();
  }, []);

  useEffect(() => {
    const loadReportData = async () => {
      setLoading(true);
      setError(null);
      try {
        const weekStartStr = format(weekStart, 'yyyy-MM-dd');
        const weekEndStr = format(weekEnd, 'yyyy-MM-dd');
        const [timecardData, employeeData] = await Promise.all([
          timecardApi.getAll(undefined, weekStartStr),
          employeeApi.getAll()
        ]);
        setTimecards(timecardData);
        setEmployees(employeeData);
      } catch (err) {
        console.error('Error loading report data:', err);
        setError('Unable to load report data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadReportData();
  }, [selectedLocationId, weekStart, weekEnd]);

  const scheduleOptions = useMemo(() => {
    const baseSchedules = selectedLocationId
      ? schedules.filter(schedule => !schedule.locationIds || schedule.locationIds.includes(selectedLocationId))
      : schedules;
    return [...baseSchedules].sort((a, b) => a.name.localeCompare(b.name));
  }, [schedules, selectedLocationId]);

  const selectedSchedule = useMemo(
    () => schedules.find(schedule => schedule.id === selectedScheduleId) || null,
    [schedules, selectedScheduleId]
  );
  const selectedScheduleCode = selectedSchedule ? getTwoLetterCode(selectedSchedule.name) : null;
  const allowedScheduleCodes = useMemo(() => {
    return new Set(scheduleOptions.map(schedule => getTwoLetterCode(schedule.name)));
  }, [scheduleOptions]);

  const filteredTimecards = useMemo(() => {
    return timecards.filter(timecard => {
      const employee = employees.find(emp => emp.id === timecard.employeeId);
      if (!employee) return false;
      if (selectedLocationId && getEmployeeLocationId(employee) !== selectedLocationId) {
        return false;
      }
      return true;
    });
  }, [timecards, employees, selectedLocationId]);

  const planHoursAll = useMemo(() => {
    const interval = { start: weekStart, end: weekEnd };
    return filteredTimecards.reduce((total, timecard) => {
      const timecardTotal = timecard.days.reduce((sum, day) => {
        const dayDate = day.date ? parseISO(day.date) : null;
        if (!dayDate || !isWithinInterval(dayDate, interval)) {
          return sum;
        }
        const scheduleCode = day.scheduleCode ?? '';
        // When a location is selected, only filter by schedule code if the day actually has a schedule code.
        // Days with null/empty scheduleCode but valid scheduled times should still be counted.
        if (
          selectedLocationId &&
          allowedScheduleCodes.size > 0 &&
          scheduleCode &&
          !allowedScheduleCodes.has(scheduleCode)
        ) {
          return sum;
        }
        return sum + getScheduledHours(day);
      }, 0);
      return total + timecardTotal;
    }, 0);
  }, [filteredTimecards, weekStart, weekEnd, selectedLocationId, allowedScheduleCodes]);

  const planHoursSelected = useMemo(() => {
    if (selectedScheduleId === 'all') {
      return planHoursAll;
    }
    return filteredTimecards.reduce((total, timecard) => {
      const timecardTotal = timecard.days.reduce((sum, day) => {
        if (!selectedScheduleCode) return sum;
        if ((day.scheduleCode ?? '') !== selectedScheduleCode) {
          return sum;
        }
        return sum + getScheduledHours(day);
      }, 0);
      return total + timecardTotal;
    }, 0);
  }, [filteredTimecards, selectedScheduleId, selectedScheduleCode, planHoursAll]);

  const actualHoursAll = useMemo(() => {
    const interval = { start: weekStart, end: weekEnd };
    return filteredTimecards.reduce((total, timecard) => {
      const timecardTotal = timecard.days.reduce((sum, day) => {
        const dayDate = day.date ? parseISO(day.date) : null;
        if (!dayDate || !isWithinInterval(dayDate, interval)) {
          return sum;
        }
        const scheduleCode = day.scheduleCode ?? '';
        // When a location is selected, only filter by schedule code if the day actually has a schedule code.
        // Days with null/empty scheduleCode but valid totalHours should still be counted.
        if (
          selectedLocationId &&
          allowedScheduleCodes.size > 0 &&
          scheduleCode &&
          !allowedScheduleCodes.has(scheduleCode)
        ) {
          return sum;
        }
        return sum + getActualPaidHours(day);
      }, 0);
      return total + timecardTotal;
    }, 0);
  }, [filteredTimecards, weekStart, weekEnd, selectedLocationId, allowedScheduleCodes]);

  const actualHoursSelected = useMemo(() => {
    const interval = { start: weekStart, end: weekEnd };
    return filteredTimecards.reduce((total, timecard) => {
      const timecardTotal = timecard.days.reduce((dayTotal, day) => {
        const dayDate = day.date ? parseISO(day.date) : null;
        if (!dayDate || !isWithinInterval(dayDate, interval)) {
          return dayTotal;
        }
        if (selectedScheduleId !== 'all') {
          if ((day.scheduleCode ?? '') !== selectedScheduleCode) {
            return dayTotal;
          }
        }
        return dayTotal + getActualPaidHours(day);
      }, 0);
      return total + timecardTotal;
    }, 0);
  }, [filteredTimecards, selectedScheduleId, selectedScheduleCode, weekStart, weekEnd]);

  const dailyTotals = useMemo(() => {
    const days = Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
    return days.map(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const plan = filteredTimecards.reduce((total, timecard) => {
        const dayTotal = timecard.days.reduce((sum, dayEntry) => {
          if (!dayEntry.date) return sum;
          const dayDateStr = format(parseISO(dayEntry.date), 'yyyy-MM-dd');
          if (dayDateStr !== dateStr) return sum;
          if (selectedScheduleId !== 'all') {
            if ((dayEntry.scheduleCode ?? '') !== selectedScheduleCode) {
              return sum;
            }
          } else if (
            selectedLocationId &&
            allowedScheduleCodes.size > 0 &&
            dayEntry.scheduleCode &&
            !allowedScheduleCodes.has(dayEntry.scheduleCode)
          ) {
            return sum;
          }
          return sum + getScheduledHours(dayEntry);
        }, 0);
        return total + dayTotal;
      }, 0);
      const actual = filteredTimecards.reduce((total, timecard) => {
        const dayTotal = timecard.days.reduce((sum, dayEntry) => {
          if (!dayEntry.date) return sum;
          const dayDateStr = format(parseISO(dayEntry.date), 'yyyy-MM-dd');
          if (dayDateStr !== dateStr) return sum;
          if (selectedScheduleId !== 'all') {
            if ((dayEntry.scheduleCode ?? '') !== selectedScheduleCode) {
              return sum;
            }
          } else if (
            selectedLocationId &&
            allowedScheduleCodes.size > 0 &&
            dayEntry.scheduleCode &&
            !allowedScheduleCodes.has(dayEntry.scheduleCode)
          ) {
            return sum;
          }
          return sum + getActualPaidHours(dayEntry);
        }, 0);
        return total + dayTotal;
      }, 0);
      return { date: day, dateStr, plan, actual };
    });
  }, [weekStart, filteredTimecards, selectedScheduleId, selectedScheduleCode, selectedLocationId, allowedScheduleCodes]);

  const planDisplayHours = selectedScheduleId === 'all' ? planHoursAll : planHoursSelected;
  const actualDisplayHours = selectedScheduleId === 'all' ? actualHoursAll : actualHoursSelected;
  const overHours = actualDisplayHours - planDisplayHours;

  return (
    <div className="w-full max-w-[95%] mx-auto p-6 py-8 h-full flex flex-col min-h-0">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-neutral-900">Plan vs Actual</h1>
              <p className="text-sm text-neutral-500">
                Week of {format(weekStart, 'MMM d, yyyy')} – {format(weekEnd, 'MMM d, yyyy')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-neutral-700">Schedules</label>
              <select
                value={selectedScheduleId}
                onChange={(event) => {
                  const value = event.target.value;
                  setSelectedScheduleId(value === 'all' ? 'all' : Number(value));
                }}
                className="w-52 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Schedules</option>
                {scheduleOptions.map(schedule => (
                  <option key={schedule.id} value={schedule.id}>
                    {schedule.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setCurrentWeek(addDays(currentWeek, -7))}
              className="px-3 py-1.5 text-sm border border-neutral-300 rounded-md bg-white hover:bg-neutral-50 transition-colors"
            >
              ← Previous Week
            </button>
            <button
              type="button"
              onClick={() => setCurrentWeek(addDays(currentWeek, 7))}
              className="px-3 py-1.5 text-sm border border-neutral-300 rounded-md bg-white hover:bg-neutral-50 transition-colors"
            >
              Next Week →
            </button>
            <button
              type="button"
              onClick={() => setCurrentWeek(new Date())}
              className="px-3 py-1.5 text-sm text-blue-600 border border-blue-200 rounded-md bg-blue-50 hover:bg-blue-100 transition-colors"
            >
              Current Week
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto" />
              <p className="mt-3 text-sm text-neutral-500">Loading report...</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            {error}
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-6 w-full lg:w-3/5 mx-auto">
            {overHours > 0 && (
              <div className="flex justify-end mb-4">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-50 text-rose-600 text-sm font-semibold border border-rose-100">
                  ⚡ {Math.round(overHours)}h Over
                </span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <CircleCard
                title="Plan"
                hours={planDisplayHours}
                subtitle="Planned Hours"
                totalHours={selectedScheduleId === 'all' ? undefined : planHoursAll}
                highlightHours={selectedScheduleId === 'all' ? undefined : planHoursSelected}
                lightColor="#bfdbfe"
                darkColor="#60a5fa"
              />
              <CircleCard
                title="Actual"
                hours={actualDisplayHours}
                subtitle="Actual Hours"
                totalHours={selectedScheduleId === 'all' ? undefined : actualHoursAll}
                highlightHours={selectedScheduleId === 'all' ? undefined : actualHoursSelected}
                lightColor="#bbf7d0"
                darkColor="#34d399"
              />
            </div>
            <div className="mt-8 border-t border-neutral-200 pt-6">
              <div className="flex items-center justify-between gap-4 mb-4">
                <div className="text-sm font-semibold text-neutral-700">Daily breakdown</div>
                <div className="flex items-center gap-4 text-xs text-neutral-500">
                  <span className="flex items-center gap-2">
                    <span className="h-2.5 w-4 rounded-sm bg-[#60a5fa]" />
                    Planned ≥ Actual
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="h-2.5 w-4 rounded-sm bg-[#34d399]" />
                    Actual &gt; Plan
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
                {dailyTotals.map(day => {
                  const isPlanOver = day.plan >= day.actual;
                  const barColor = isPlanOver ? 'bg-[#60a5fa]' : 'bg-[#34d399]';
                  return (
                    <div
                      key={day.dateStr}
                      className="relative flex flex-col items-center gap-2"
                      onMouseEnter={() => setHoveredDay(day.dateStr)}
                      onMouseLeave={() => setHoveredDay(null)}
                    >
                      {hoveredDay === day.dateStr && (
                        <div className="absolute -top-24 z-10 w-56 rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm shadow-lg">
                          <div className="text-base font-semibold text-neutral-800">
                            {format(day.date, 'dd MMM yyyy')}
                          </div>
                          <div className="mt-2 flex items-center gap-2 text-neutral-700">
                            <span className="text-base" aria-hidden>📅</span>
                            <span className="font-medium">Planned:</span>
                            <span>{day.plan.toFixed(2)}h</span>
                          </div>
                          <div className="mt-2 flex items-center gap-2 text-neutral-700">
                            <span className="text-base" aria-hidden>🕒</span>
                            <span className="font-medium">Actual:</span>
                            <span>{day.actual.toFixed(2)}h</span>
                          </div>
                        </div>
                      )}
                      <div className="w-full max-w-[90px]">
                        <div className={`h-4 rounded ${barColor}`} />
                      </div>
                      <div className="text-xs text-neutral-500 text-center">
                        {format(day.date, 'dd MMM')}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanVsActual;
